import { memo, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useMobileVerificationContext } from "@zenith/context";
import { AppNavigation } from "@zenith/hooks";
import { OTPComponent } from "@zenith/components";
import { CREATE_NEW_PIN, FORGOT_PIN, NEW_USER_PIN } from "@zenith/routes/paths";
import { Button } from "@mui/material";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import {
  DEFAULT_PURPOSE,
  PASSCODE_CHANGE_OTP_PURPOSE,
  TRX_PURPOSE,
} from "@zenith/data/constants";

/* eslint-disable-next-line */
export interface MobileVerificationPageProps {}

const MobileVerificationPage = memo(function MobileVerificationPage(
  props: MobileVerificationPageProps
) {
  const { navigateTo } = AppNavigation();
  const location = useLocation() as {
    state?: { noRegistrationType?: boolean };
  };
  const noRegistrationType = location.state?.noRegistrationType === true;

  const {
    mobileVerificationContext: { countryCode, phoneNumber, isNewUser, email },
  } = useMobileVerificationContext();

  useEffect(() => {
    if (!countryCode && !phoneNumber) {
      handleBackButton();
    }
  });

  function handleBackButton(): void {
    if (isNewUser) {
      navigateTo(NEW_USER_PIN);
    } else {
      navigateTo(FORGOT_PIN);
    }
  }

  const goToCreatePin = (verificationCode?: string) => {
    if (verificationCode) {
      navigateTo(CREATE_NEW_PIN, { state: { verificationCode } });
    } else {
      navigateTo(CREATE_NEW_PIN);
    }
  };

  // BOAS-8726: no registrationType on record - the reset call is authorised
  // by the X-MFA token the OTP verify returns, not by the code itself
  const goToCreatePinWithMfa = (data?: { xMfa?: string }) => {
    navigateTo(CREATE_NEW_PIN, {
      state: { noRegistrationType: true, xMfa: data?.xMfa },
    });
  };

  return (
    <div className="w-full ">
      <div className="flex grow flex-col justify-center md:mx-10">
        <div className="mb-10 flex items-start sm:mt-0 lg:mt-20">
          <Button
            id="go-back-to-forgot-pin"
            startIcon={<ChevronLeft />}
            className="rounded-lg normal-case text-black hover:bg-gray-300"
            onClick={handleBackButton}
          >
            Back
          </Button>
        </div>
      </div>
      <div className={"flex flex-col items-center justify-center"}>
        <h5 className="mb-6 text-4xl">Mobile verification</h5>
      </div>
      <div className="mx-auto flex w-full max-w-md flex-col">
        <OTPComponent
          phoneNumber={phoneNumber}
          countryCode={countryCode}
          {...(noRegistrationType
            ? { onVerifySuccessWithData: goToCreatePinWithMfa }
            : { onVerifySuccess: goToCreatePin })}
          backBtnInBottom={false}
          align="center"
          // BOAS-8726: a passcode reset sends and verifies the code as
          // CHANGE_PROFILE_PASSWORD; first-time setup keeps the default.
          OTPPurpose={
            isNewUser ? DEFAULT_PURPOSE : PASSCODE_CHANGE_OTP_PURPOSE
          }
          email={email}
        />
      </div>
    </div>
  );
});

export default MobileVerificationPage;
